export type CSVRow = Record<string, string>

const CANDIDATE_DELIMITERS = [",", ";", "\t", "|", ":"];

export interface ParseOptions {
    delimiter?: string
    hasHeader?: boolean
    trim?: boolean
    dynamicTyping?: boolean;
}

export interface CSVResult<T extends object = CSVRow> {
    headers: string[]
    rows: T[]
    valid?: boolean
    errors?: CSVParseError[]
}

export type CSVParseError =
    | { type: "UNTERMINATED_QUOTE"; position: number }
    | { type: "INCONSISTENT_COLUMNS"; row: number; expected: number; actual: number }

interface InternalParseOk {
    rowsRaw: string[][]
}

interface InternalParseResult {
    ok: boolean
    value: InternalParseOk
    errors?: CSVParseError[]
}


const detectDelimiter = (input: string): string => {
      const lines = input
        .split(/\r\n|\n|\r/)
        .filter(l => l.trim().length > 0)
        .slice(0, 5)

    if (lines.length === 0) return ","

    let bestDelimiter = ","
    let bestScore = -1

    for (const d of CANDIDATE_DELIMITERS) {
        const counts = lines.map(l => l.split(d).length)
        const uniqueCounts = new Set(counts)
        const score = uniqueCounts.size === 1 && counts[0] > 1 ? counts[0] : -1
        if (score > bestScore) {
            bestScore = score
            bestDelimiter = d
        }
    }

    return bestDelimiter
}

const castValue = (raw: string): string | number | boolean | null => {
    const value = raw.trim()
    if (value === "") return null

    // booleans
    if (/^(true|false)$/i.test(value)) {
        return value.toLowerCase() === "true"
    }

    // numbers (int or float)
    const num = Number(value);
    if (!Number.isNaN(num) && value !== "") {
        return num
    }

    return value
}


const internalParseCSV = (
    input: string,
    delimiter: string
): InternalParseResult => {

    const rowsRaw: string[][] = []
    let currentRow: string[] = []
    let currentField = ""
    let inQuotes = false
    let expectedColumns: number | undefined
    const errors: CSVParseError[] = []

    for (let i = 0; i < input.length; i++) {
        const char = input[i]
        const next = input[i + 1]

        if (char === '"') {
            if (inQuotes && next === '"') {
                currentField += '"'
                i++
            } else {
                inQuotes = !inQuotes
            }
        } else if (char === delimiter && !inQuotes) {
            currentRow.push(currentField)
            currentField = ""
        } else if ((char === "\n" || char === "\r") && !inQuotes) {
            if (char === "\r" && next === "\n") {
                i++
            }
            currentRow.push(currentField)

            if (currentRow.length > 1 || currentRow[0] !== "") {
                if (expectedColumns === undefined) {
                    expectedColumns = currentRow.length
                } else if (currentRow.length !== expectedColumns) {
                    errors.push({
                        type: "INCONSISTENT_COLUMNS",
                        row: rowsRaw.length + 1,
                        expected: expectedColumns,
                        actual: currentRow.length
                    })
                }
                rowsRaw.push(currentRow)
            }

            currentRow = []
            currentField = ""
        } else {
            currentField += char
        }
    }

    if (currentField.length > 0 || currentRow.length > 0) {
        currentRow.push(currentField)
        if (expectedColumns === undefined) {
            expectedColumns = currentRow.length
        } else if (currentRow.length !== expectedColumns) {
            errors.push({
                type: "INCONSISTENT_COLUMNS",
                row: rowsRaw.length + 1,
                expected: expectedColumns,
                actual: currentRow.length
            })
        }
        rowsRaw.push(currentRow)
    }

    if (inQuotes) {
        errors.push({
            type: "UNTERMINATED_QUOTE",
            position: input.length - 1
        })
    }

    if (errors.length > 0) {
        return { ok: false, value: { rowsRaw }, errors }
    }
    return { ok: true, value: { rowsRaw } }
}


export const parseCSV = <T extends object = CSVRow>(
    input: string,
    options: ParseOptions = {}
): CSVResult<T> => {

    const delimiter = options.delimiter ?? detectDelimiter(input)

    if (!looksLikeCSV(input, delimiter)) {
        return { headers: [], rows: [], valid: false, errors: [] }
    }
    
    const hasHeader = options.hasHeader ?? true
    const trim = options.trim ?? true
    const dynamicTyping = options.dynamicTyping ?? true


    const parsed = internalParseCSV(input, delimiter)
    const { rowsRaw } = parsed.value

    if (rowsRaw.length === 0) {
        return {
            headers: [],
            rows: [],
            valid: parsed.ok,
            errors: parsed.ok ? [] : parsed.errors
        }
    }

    const headerRow = hasHeader ? rowsRaw[0] : undefined
    const dataRows = hasHeader ? rowsRaw.slice(1) : rowsRaw

    const rawHeaders = headerRow
        ? headerRow.map(h => (trim ? h.trim() : h))
        : dataRows[0]
        ? dataRows[0].map((_, i) => `col${i}`)
        : []

    // keep only non-empty headers, but remember source index
    const headerDefs = rawHeaders
        .map((h, i) => ({ name: h, index: i }))
        .filter(h => h.name !== "")

    const headers = headerDefs.map(h => h.name)

    const rows = dataRows.map((row) => {
        const obj: any = {}
        headerDefs.forEach(({ name, index }) => {
            let value: unknown = row[index] ?? ""
            if (trim && typeof value === "string") {
                value = value.trim()
            }
            obj[name] = dynamicTyping ? castValue(String(value)) : value
        })
        return obj as T
    })

    return {
        headers,
        rows,
        valid: parsed.ok,
        errors: parsed.ok ? [] : parsed.errors
    }
}

const looksLikeCSV = (input: string, delimiter: string): boolean  => {
    const trimmed = input.trim()
    if (trimmed === "") return false

    // at least one delimiter AND at least 3 lines
    const hasDelimiter = trimmed.includes(delimiter)
    const lines = trimmed.split(/\r\n|\n|\r/)
    const nonEmptyLines = lines.filter(l => l.trim() !== "")
    return hasDelimiter && nonEmptyLines.length > 2
}

export const downloadFileResult = (fileContent: string, fileName: string) => {
    var element = document.createElement('a')
    element.setAttribute('href', 'data:text/plain;charset=utf-8,' + encodeURIComponent(fileContent))
    element.setAttribute('download', fileName)

    element.style.display = 'none'
    document.body.appendChild(element)

    element.click()

    document.body.removeChild(element)
}


export const getFileName = (file: File) => {
    const fileName = file.name
    return (
        fileName.substring(0, fileName.lastIndexOf('.')) || fileName
    )
}
